const SERVICE_PORTS = ['443', '80', '22', '3389', '25', '587', '1433', '3306', '5432', '8080', '8443'];

export function parseAdditionalData(f) {
  if (f?.additionalData && typeof f.additionalData === 'object') return f.additionalData;
  const raw = f?.detail?.additionalData ?? f?.additionalData;
  if (!raw) return {};
  if (typeof raw === 'string') {
    try { return JSON.parse(raw); } catch { return {}; }
  }
  return typeof raw === 'object' ? raw : {};
}

function allFindingText(f) {
  const ad = parseAdditionalData(f);
  return [
    f?.title,
    f?.asset,
    f?.module,
    f?.fingerprint,
    f?.description,
    f?.detail?.affectedComponent,
    f?.detail?.sourceDomain,
    f?.detail?.sourceApp,
    ...Object.values(ad),
  ].filter(Boolean).join(' ');
}

function uniq(values) {
  const seen = new Set();
  const out = [];
  for (const value of values) {
    const text = String(value ?? '').trim();
    if (!text) continue;
    const key = text.toLowerCase();
    if (seen.has(key)) continue;
    seen.add(key);
    out.push(text);
  }
  return out;
}

function normalizeHost(host) {
  const clean = String(host ?? '')
    .toLowerCase()
    .replace(/^https?:\/\//, '')
    .split('/')[0]
    .split(':')[0]
    .trim()
    .replace(/^\*\./, '')
    .replace(/\.$/, '');
  return clean;
}

export function domainOfHost(host) {
  const clean = normalizeHost(host);
  const parts = clean.split('.').filter(Boolean);
  if (parts.length < 2) return '';
  return parts.slice(-2).join('.');
}

function normalizeDomainList(values) {
  return uniq(values)
    .flatMap(value => String(value).split(/[,\s;]+/))
    .map(normalizeHost)
    .filter(value => value && value.includes('.') && !/^\d+\.\d+\.\d+\.\d+$/.test(value));
}

function associatedDomainSeeds(findings, configuredDomains = []) {
  const discovered = [];
  for (const finding of findings) {
    const ad = parseAdditionalData(finding);
    discovered.push(
      ad.clientDomain,
      ad.primaryDomain,
      ad.seedDomain,
      ad.rootDomain,
      finding?.detail?.clientDomain,
      finding?.detail?.primaryDomain
    );
  }
  return normalizeDomainList([...configuredDomains, ...discovered]);
}

export function extractHosts(f) {
  const ad = parseAdditionalData(f);
  const text = allFindingText(f);
  const explicit = [
    ad.host, ad.hostname, ad.hostName, ad.fqdn, ad.domain, ad.subdomain, ad.sourceDomain,
    f?.asset, f?.detail?.affectedComponent, f?.detail?.sourceDomain,
  ];
  const urls = [...text.matchAll(/https?:\/\/([a-z0-9.-]+\.[a-z]{2,})(?::\d+)?/gi)].map(m => m[1]);
  const names = [...text.matchAll(/\b([a-z0-9][a-z0-9-]*(?:\.[a-z0-9][a-z0-9-]*)*\.[a-z]{2,})\b/gi)].map(m => m[1]);
  return uniq([...explicit, ...urls, ...names])
    .map(normalizeHost)
    .filter(h => h && !/^\d+\.\d+\.\d+\.\d+$/.test(h));
}

export function extractIps(f) {
  const ad = parseAdditionalData(f);
  const text = allFindingText(f);
  return uniq([
    ad.ip, ad.ipAddress, ad.hostIp,
    ...text.match(/\b(?:\d{1,3}\.){3}\d{1,3}\b/g) ?? [],
  ]);
}

export function extractServices(f) {
  const ad = parseAdditionalData(f);
  const text = allFindingText(f).toLowerCase();
  const ports = uniq([
    ad.port, ad.servicePort,
    ...[...text.matchAll(/(?:port|:)\s*(\d{2,5})\b/g)].map(m => m[1]),
  ]).filter(p => SERVICE_PORTS.includes(String(p)));
  const named = [];
  if (/https|tls|ssl|certificate|443/.test(text)) named.push('HTTPS');
  if (/\bhttp\b|:80\b/.test(text)) named.push('HTTP');
  if (/ssh|:22\b/.test(text)) named.push('SSH');
  if (/rdp|3389/.test(text)) named.push('RDP');
  if (/smtp|mail|:25\b|:587\b/.test(text)) named.push('Mail');
  if (/mysql|postgres|mssql|sql|1433|3306|5432/.test(text)) named.push('Database');
  return uniq([...named, ...ports.map(p => `:${p}`)]);
}

function belongsToAssociatedDomain(host, seeds) {
  const value = normalizeHost(host);
  if (!value) return false;
  return seeds.some(seed => value === seed || value.endsWith(`.${seed}`));
}

function addDomainNode(domains, host, findingIps, findingServices) {
  const root = domainOfHost(host) || host;
  if (!domains.has(root)) domains.set(root, { name: root, subdomains: new Set(), ips: new Set(), services: new Set() });
  const node = domains.get(root);
  if (host !== root) node.subdomains.add(host);
  findingIps.forEach(ip => node.ips.add(ip));
  findingServices.forEach(service => node.services.add(service));
}

function toDomainArray(domains) {
  return [...domains.values()].map(d => ({
    name: d.name,
    subdomains: [...d.subdomains],
    ips: [...d.ips],
    services: [...d.services],
  }));
}

export function buildAttackSurface(findings, options = {}) {
  const seeds = associatedDomainSeeds(findings, options.associatedDomains ?? []);
  const shouldFilterByAssociation = seeds.length > 0;
  const domains = new Map();
  const separatedDomains = new Map();
  const ips = new Set();
  const services = new Set();

  for (const finding of findings) {
    const hosts = extractHosts(finding);
    const findingIps = extractIps(finding);
    const findingServices = extractServices(finding);

    for (const host of hosts) {
      if (shouldFilterByAssociation && !belongsToAssociatedDomain(host, seeds)) {
        addDomainNode(separatedDomains, host, findingIps, findingServices);
        continue;
      }

      addDomainNode(domains, host, findingIps, findingServices);
      findingIps.forEach(ip => ips.add(ip));
      findingServices.forEach(service => services.add(service));
    }
  }

  return {
    domains: toDomainArray(domains),
    separatedDomains: toDomainArray(separatedDomains),
    ips: [...ips],
    services: [...services],
    associatedDomains: seeds,
  };
}
